import React, { useRef, lazy } from 'react';
import { motion, useInView } from 'framer-motion';
import { BackpackIcon, SunIcon, ArrowRightIcon, PhoneIcon, MapPinIcon, ClockIcon, BusIcon } from 'lucide-react';
const afterSchoolActivities = ['Martial Arts', 'Outdoor Play', 'Bounce Houses', 'Dance', 'Team Sports', 'Arts & Crafts', 'Games', 'Leadership Training', 'Social Development'];
const summerCampActivities = ['Indoor Sports', 'Outdoor Sports', 'Educational Lessons', 'Arts & Crafts', 'Martial Arts', 'Outdoor Play', 'Bounce Houses', 'Dance', 'Team Sports'];
const schoolsServed = ['Heritage', 'Glenhope', 'OC Taylor', 'Bransford', 'Bedford Heights', 'WA Porter', 'Walker Creek', 'Smithfield'];
export function AdditionalPrograms() {
  const ref = useRef(null);
  const inView = useInView(ref, {
    once: true,
    margin: '-80px'
  });
  return <section className="py-20 md:py-28 bg-warm-100" data-id="element-6">
      <div className="max-w-7xl mx-auto px-4 sm:px-6" ref={ref} data-id="element-7">
        {/* Section header */}
        <motion.div className="text-center max-w-4xl mx-auto mb-14 md:mb-16" initial={{
        opacity: 0,
        y: 24
      }} animate={inView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.5
      }} data-id="element-8">
          <h2 className="text-3xl sm:text-4xl font-extrabold text-navy-950 font-heading tracking-tight" data-id="element-9">
            Keep Kids Active Year-Round
          </h2>
          <p className="mt-4 text-lg text-ink-muted leading-relaxed" data-id="element-10">
            Safe, structured programs that fit your family schedule.
          </p>
        </motion.div>

        <div className="space-y-12 max-w-5xl mx-auto" data-id="element-11">
          {/* After School Program Block */}
          <motion.div className="bg-white rounded-2xl overflow-hidden border border-warm-200 shadow-sm" initial={{
          opacity: 0,
          y: 24
        }} animate={inView ? {
          opacity: 1,
          y: 0
        } : {}} transition={{
          duration: 0.4,
          delay: 0.1
        }} data-id="element-12">
            <div className="grid grid-cols-1 lg:grid-cols-5" data-id="element-13">
              {/* Image — takes 2 of 5 cols on desktop */}
              <div className="lg:col-span-2" data-id="element-14">
                <img src="https://cdn.magicpatterns.com/uploads/xphyd2NfVnX5mivfmQRQcV/After-School-Program.webp" alt="Kids smiling with backpacks for After School Program" className="w-full h-64 lg:h-full object-cover object-center" loading="lazy" data-id="element-15" />
              </div>
              {/* Content — takes 3 of 5 cols on desktop */}
              <div className="lg:col-span-3 p-8 md:p-10" data-id="element-16">
                <div className="w-12 h-12 rounded-xl bg-navy-50 border border-navy-100 flex items-center justify-center mb-5" data-id="element-17">
                  <BackpackIcon className="w-6 h-6 text-navy-700" data-id="element-18" />
                </div>
                <h3 className="text-2xl font-bold text-navy-950 font-heading mb-3" data-id="element-19">
                  After School Program
                </h3>
                <p className="text-base text-ink-muted leading-relaxed mb-6" data-id="element-20">
                  A safe, structured, and active after-school environment where
                  kids get physical activity, martial arts training, and build
                  lasting friendships.
                </p>

                <div className="space-y-3 mb-6" data-id="element-21">
                  <div className="flex items-start gap-3" data-id="element-22">
                    <ClockIcon className="w-5 h-5 text-amber shrink-0 mt-0.5" data-id="element-23" />
                    <div data-id="element-24">
                      <p className="text-sm font-bold text-navy-950" data-id="element-25">Hours</p>
                      <p className="text-sm text-ink-muted" data-id="element-26">
                        Pickup at school dismissal until 6:00 PM
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3" data-id="element-27">
                    <BusIcon className="w-5 h-5 text-amber shrink-0 mt-0.5" data-id="element-28" />
                    <div data-id="element-29">
                      <p className="text-sm font-bold text-navy-950" data-id="element-30">
                        Transportation
                      </p>
                      <p className="text-sm text-ink-muted" data-id="element-31">
                        Safe and secure daily transportation included
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3" data-id="element-32">
                    <MapPinIcon className="w-5 h-5 text-amber shrink-0 mt-0.5" data-id="element-33" />
                    <div data-id="element-34">
                      <p className="text-sm font-bold text-navy-950" data-id="element-35">
                        Location
                      </p>
                      <p className="text-sm text-ink-muted" data-id="element-36">
                        750 Grapevine Hwy., Hurst, TX 76054
                      </p>
                    </div>
                  </div>
                </div>

                {/* Schools Served */}
                <div className="mb-6" data-id="element-37">
                  <h4 className="text-xs font-bold text-navy-950 uppercase tracking-wider mb-3" data-id="element-38">
                    Schools Served
                  </h4>
                  <div className="flex flex-wrap gap-1.5" data-id="element-39">
                    {schoolsServed.map(school => <span key={school} className="inline-flex items-center px-2.5 py-1 bg-warm-100 border border-warm-200 rounded-full text-xs font-semibold text-ink" data-id="element-40">
                        {school}
                      </span>)}
                  </div>
                </div>

                {/* Activities */}
                <div className="mb-8" data-id="element-41">
                  <h4 className="text-xs font-bold text-navy-950 uppercase tracking-wider mb-3" data-id="element-42">
                    Activities
                  </h4>
                  <ul className="grid grid-cols-2 sm:grid-cols-3 gap-1.5" data-id="element-43">
                    {afterSchoolActivities.map(activity => <li key={activity} className="flex items-center gap-1.5 text-sm text-ink-muted" data-id="element-44">
                        <span className="w-1.5 h-1.5 rounded-full bg-amber-dark shrink-0" data-id="element-45" />
                        {activity}
                      </li>)}
                  </ul>
                </div>

                <a href="#cta" className="inline-flex items-center justify-center gap-2 px-6 py-3.5 bg-navy-950 text-white font-bold rounded-lg hover:bg-navy-900 transition-colors focus:outline-none focus:ring-2 focus:ring-navy-950 focus:ring-offset-2 w-full sm:w-auto" data-id="element-46">
                  Enroll Now
                  <ArrowRightIcon className="w-4 h-4" data-id="element-47" />
                </a>
              </div>
            </div>
          </motion.div>

          {/* Summer Camp Block */}
          <motion.div className="bg-white rounded-2xl overflow-hidden border border-warm-200 shadow-sm" initial={{
          opacity: 0,
          y: 24
        }} animate={inView ? {
          opacity: 1,
          y: 0
        } : {}} transition={{
          duration: 0.4,
          delay: 0.2
        }} data-id="element-48">
            <div className="grid grid-cols-1 lg:grid-cols-5" data-id="element-49">
              {/* Image — takes 2 of 5 cols on desktop */}
              <div className="lg:col-span-2" data-id="element-50">
                <img src="https://cdn.magicpatterns.com/uploads/7FUSiyxGTEos5yhYyNKiTw/Summer-Camp.png" alt="Kids giving thumbs up at Summer Camp" className="w-full h-64 lg:h-full object-cover object-center" loading="lazy" data-id="element-51" />
              </div>
              {/* Content — takes 3 of 5 cols on desktop */}
              <div className="lg:col-span-3 p-8 md:p-10" data-id="element-52">
                <div className="w-12 h-12 rounded-xl bg-amber-50 border border-amber/20 flex items-center justify-center mb-5" data-id="element-53">
                  <SunIcon className="w-6 h-6 text-amber-dark" data-id="element-54" />
                </div>
                <h3 className="text-2xl font-bold text-navy-950 font-heading mb-3" data-id="element-55">
                  Summer Camp
                </h3>
                <p className="text-base text-ink-muted leading-relaxed mb-6" data-id="element-56">
                  Keep kids active, engaged, and learning all summer long with
                  our action-packed day camp. A summer they'll actually look
                  forward to.
                </p>

                <div className="space-y-3 mb-6" data-id="element-57">
                  <div className="flex items-start gap-3" data-id="element-58">
                    <ClockIcon className="w-5 h-5 text-amber shrink-0 mt-0.5" data-id="element-59" />
                    <div data-id="element-60">
                      <p className="text-sm font-bold text-navy-950" data-id="element-61">Hours</p>
                      <p className="text-sm text-ink-muted" data-id="element-62">
                        7:30 AM – 6:00 PM
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3" data-id="element-63">
                    <MapPinIcon className="w-5 h-5 text-amber shrink-0 mt-0.5" data-id="element-64" />
                    <div data-id="element-65">
                      <p className="text-sm font-bold text-navy-950" data-id="element-66">
                        Location
                      </p>
                      <p className="text-sm text-ink-muted" data-id="element-67">
                        750 Grapevine Hwy., Hurst, TX 76054
                      </p>
                    </div>
                  </div>
                </div>

                {/* Activities */}
                <div className="mb-8" data-id="element-68">
                  <h4 className="text-xs font-bold text-navy-950 uppercase tracking-wider mb-3" data-id="element-69">
                    Camp Activities
                  </h4>
                  <ul className="grid grid-cols-2 sm:grid-cols-3 gap-1.5" data-id="element-70">
                    {summerCampActivities.map(activity => <li key={activity} className="flex items-center gap-1.5 text-sm text-ink-muted" data-id="element-71">
                        <span className="w-1.5 h-1.5 rounded-full bg-amber-dark shrink-0" data-id="element-72" />
                        {activity}
                      </li>)}
                  </ul>
                </div>

                <a href="#cta" className="inline-flex items-center justify-center gap-2 px-6 py-3.5 bg-amber text-navy-950 font-bold rounded-lg hover:bg-amber-light transition-colors focus:outline-none focus:ring-2 focus:ring-amber focus:ring-offset-2 w-full sm:w-auto" data-id="element-73">
                  Learn More
                  <ArrowRightIcon className="w-4 h-4" data-id="element-74" />
                </a>
              </div>
            </div>
          </motion.div>

          {/* Program Contact Footer */}
          <motion.div className="text-center" initial={{
          opacity: 0
        }} animate={inView ? {
          opacity: 1
        } : {}} transition={{
          duration: 0.5,
          delay: 0.3
        }} data-id="element-75">
            <div className="inline-flex flex-col sm:flex-row items-center gap-2 sm:gap-4 px-6 py-4 bg-white rounded-full border border-warm-200 shadow-sm" data-id="element-76">
              <span className="text-sm font-medium text-ink-muted" data-id="element-77">
                For After School &amp; Summer Camp info:
              </span>
              <a href="tel:214-885-2295" className="text-navy-950 font-bold hover:text-amber-dark transition-colors flex items-center gap-1.5" data-id="element-78">
                <PhoneIcon className="w-4 h-4" data-id="element-79" />
                Call 214-885-2295
              </a>
            </div>
          </motion.div>
        </div>
      </div>
    </section>;
}